import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { getAttribution, getVisitorId, trackEvent } from "@/lib/analytics";
import { useLeadForm, leadField } from "@/lib/leadForms";
import { useAuth } from "@/context/AuthContext";
import ConsentCheckbox, { CONSENT_TEXT, validateConsent } from "@/components/ConsentCheckbox";
import PhoneInput from "@/components/PhoneInput";

export default function CallbackModal({ open, onOpenChange, programSlug = "", programTitle = "" }) {
  const { user } = useAuth();
  const cfg = useLeadForm("callback", open);
  const [programs, setPrograms] = useState([]);
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "", email: user?.email || "", preferred_time: "Anytime", program_slug: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [consent, setConsent] = useState(true);

  useEffect(() => { api.get("/programs", { params: { page_size: 100 } }).then((r) => setPrograms(r.data.programs || r.data || [])).catch(() => {}); }, []);

  useEffect(() => {
    if (open) setForm((f) => ({ ...f, program_slug: programSlug || "" }));
  }, [open, programSlug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!validateConsent(consent)) return;
    if (fCourse.visible && fCourse.required && !form.program_slug) { toast.error("Please select a course"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/callbacks", { ...form, program_slug: form.program_slug || programSlug, visitor_id: getVisitorId(), consent_given: consent, consent_text: CONSENT_TEXT, attribution: { ...getAttribution(), program_title: programTitle, page: window.location.pathname } });
      trackEvent("callback_request", { program_slug: form.program_slug || programSlug });
      setDone(true);
      toast.success(data.message);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const fName = leadField(cfg, "name", { label: "Full name", required: true });
  const fPhone = leadField(cfg, "phone", { label: "Phone number", required: true });
  const fEmail = leadField(cfg, "email", { label: "Email (optional)" });
  const fTime = leadField(cfg, "preferred_time", { label: "Preferred time" });
  const fCourse = leadField(cfg, "program_slug", { label: "Select Course", required: true });

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setDone(false); }}>
      <DialogContent className="sm:max-w-md" data-testid="callback-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">{cfg.title || "Get a free counseling call"}</DialogTitle>
          <DialogDescription>{programTitle ? `About: ${programTitle}` : (cfg.subtitle || "Career guidance on programs, fees, EMI and placements.")}</DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="py-8 text-center" data-testid="callback-success">
            <div className="w-14 h-14 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">✓</div>
            <p className="font-semibold text-slate-900">{cfg.success_title || "Callback requested"}</p>
            <p className="text-sm text-slate-500 mt-1">{cfg.success_message || "A counselor will call you at your preferred time."}</p>
            <Button className="mt-6 bg-rose-600 hover:bg-rose-700 rounded-xl" onClick={() => onOpenChange(false)} data-testid="callback-done-close">Done</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-4 mt-2">
            {fName.visible && (
              <div><Label htmlFor="cb-name">{fName.label}{fName.required && " *"}</Label>
                <Input id="cb-name" required={!!fName.required} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-xl" data-testid="callback-name-input" /></div>
            )}
            {fPhone.visible && (
              <div><Label htmlFor="cb-phone">{fPhone.label}{fPhone.required && " *"}</Label>
                <PhoneInput id="cb-phone" required={!!fPhone.required} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} wrapperCls="mt-1.5" inputCls="w-full h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-[#FFC72C]" testid="callback-phone-input" /></div>
            )}
            {fEmail.visible && (
              <div><Label htmlFor="cb-email">{fEmail.label}{fEmail.required && " *"}</Label>
                <Input id="cb-email" type="email" required={!!fEmail.required} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 rounded-xl" data-testid="callback-email-input" /></div>
            )}
            {fCourse.visible && (
              <div><Label htmlFor="cb-course">{fCourse.label}{fCourse.required && " *"}</Label>
                <div className="relative mt-1.5">
                  <select id="cb-course" required={!!fCourse.required} value={form.program_slug}
                    onChange={(e) => setForm({ ...form, program_slug: e.target.value })}
                    className={`w-full h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm appearance-none outline-none focus:ring-2 focus:ring-[#FFC72C] ${form.program_slug ? "" : "text-slate-400"}`}
                    data-testid="callback-course-select">
                    <option value="">Select Course</option>
                    {programs.map((pr) => <option key={pr.slug} value={pr.slug}>{pr.title}</option>)}
                  </select>
                </div></div>
            )}
            {fTime.visible && (
              <div><Label>{fTime.label}</Label>
                <Select value={form.preferred_time} onValueChange={(v) => setForm({ ...form, preferred_time: v })}>
                  <SelectTrigger className="mt-1.5 rounded-xl" data-testid="callback-time-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Anytime", "Morning (9am–12pm)", "Afternoon (12–4pm)", "Evening (4–8pm)"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select></div>
            )}
            <ConsentCheckbox checked={consent} onChange={setConsent} testid="callback-consent" />
            <Button type="submit" disabled={loading} className="bg-rose-600 hover:bg-rose-700 rounded-xl font-semibold" data-testid="callback-submit-button">
              {loading ? "Requesting…" : (cfg.button_label || "Request callback")}
            </Button>
            <p className="text-[11px] text-slate-400 text-center">Or call us directly: +91 9015 887 887</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
